/* Make a video. Turns a member's rendered mockups into a short stop-motion clip, made in the browser.
   Nothing is sent anywhere and no Dynamic Mockups credits are used: the renders already exist.
   Open with MeMotion.open({ images: [{ url: '...', name: 'Newspaper Cafe 01' }] }).
   Image URLs must allow canvas reads (the worker's /img sends Access-Control-Allow-Origin: *),
   otherwise the preview plays but the MP4 export is blocked by the browser.
   Export: WebCodecs + mp4-muxer (loaded only when needed), MediaRecorder as the fallback. */
(function () {
  'use strict';

  var MUXER_SRC = 'https://cdn.jsdelivr.net/npm/mp4-muxer@5.2.1/build/mp4-muxer.min.js';
  var MP4BOX_SRC = 'https://cdn.jsdelivr.net/npm/mp4box@0.5.2/dist/mp4box.all.min.js';
  var MAX = 10;
  var INK = '#141414', WHITE = '#ffffff', DOT = '#f4d9e3';
  var SIZES = [
    { id: '9x16', W: 1080, H: 1920, label: '9:16' },
    { id: '4x5', W: 1080, H: 1350, label: '4:5' },
    { id: '1x1', W: 1080, H: 1080, label: '1:1' },
    { id: '16x9', W: 1920, H: 1080, label: '16:9' }
  ];
  var MOVES = [
    { id: 'drop', label: 'Drop' },
    { id: 'flip', label: 'Flipbook' },
    { id: 'slide', label: 'Slide in' },
    { id: 'bounce', label: 'Bounce' }
  ];
  var PACES = [
    { id: 'quick', label: 'Quick', s: .55 },
    { id: 'steady', label: 'Steady', s: .9 },
    { id: 'slow', label: 'Slow', s: 1.35 }
  ];
  var FEELS = [
    { id: 'choppy', label: 'Choppy', fps: 8 },
    { id: 'classic', label: 'Classic', fps: 12 }
  ];
  var GROUNDS = [
    { id: 'white', c: '#ffffff', label: 'White' },
    { id: 'grey', c: '#eeeeee', label: 'Grey' },
    { id: 'blush', c: '#f9f1f4', label: 'Blush' },
    { id: 'ink', c: '#141414', label: 'Black' }
  ];

  /* ------------------------------------------------------------------ helpers */
  function R(a, b, c) {
    var x = Math.sin(a * 127.1 + (b || 0) * 311.7 + (c || 0) * 74.7 + 13.37) * 43758.5453;
    return x - Math.floor(x);
  }
  function find(list, id) { for (var i = 0; i < list.length; i++) { if (list[i].id === id) { return list[i]; } } return list[0]; }
  function el(tag, cls, html) { var e = document.createElement(tag); if (cls) { e.className = cls; } if (html != null) { e.innerHTML = html; } return e; }
  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  function fileLabel(name) { return (name || 'mockup').toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '') + '.JPG'; }
  function loadScript(src) {
    return new Promise(function (res, rej) { var s = document.createElement('script'); s.src = src; s.onload = res; s.onerror = rej; document.head.appendChild(s); });
  }
  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    if (ctx.roundRect) { ctx.roundRect(x, y, w, h, r); return; }
    ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
  }
  function setSpacing(ctx, px) { if ('letterSpacing' in ctx) { ctx.letterSpacing = px + 'px'; } }

  var grain = (function () {
    var list = [];
    for (var n = 0; n < 3; n++) {
      var c = document.createElement('canvas'); c.width = c.height = 256;
      var g = c.getContext('2d'), d = g.createImageData(256, 256);
      for (var i = 0; i < d.data.length; i += 4) { var v = Math.random() * 255; d.data[i] = d.data[i + 1] = d.data[i + 2] = v; d.data[i + 3] = Math.random() * 120; }
      g.putImageData(d, 0, 0); list.push(c);
    }
    return list;
  })();

  /* ------------------------------------------------------------------ drawing
     Everything is drawn in output pixels (W x H). U is the short side. SC is how much the
     context is scaled on screen, so shadow blur (which ignores transforms) stays consistent. */
  function Painter(state) {
    var U = 1080, SC = 1;

    function timing() {
      var fps = find(FEELS, state.feel).fps, frames = on();
      var n = frames.length;
      var hold = Math.max(5, Math.round(fps * find(PACES, state.pace).s));
      var fin = n ? n + 5 + Math.round(fps * 2.2) : 0;
      return { F: fps, n: n, hold: hold, fin: fin, total: n ? n * hold + fin : 1, frames: frames };
    }
    function on() { return state.frames.filter(function (f) { return f.on && f.img; }); }
    function wob(f, k) {
      var b = .6;
      return { x: (R(f, k, 1) - .5) * b * .011 * U, y: (R(f, k, 2) - .5) * b * .011 * U, r: (R(f, k, 3) - .5) * b * .03 };
    }
    function fit(fr, size) { var a = fr.w / fr.h; return a >= 1 ? { w: size, h: size / a } : { w: size * a, h: size }; }
    function shadow(ctx, blur, oy, a) { ctx.shadowColor = 'rgba(20,20,20,' + a + ')'; ctx.shadowBlur = blur * SC; ctx.shadowOffsetX = 0; ctx.shadowOffsetY = oy * SC; }
    function noShadow(ctx) { ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0; }
    function fg(bg) { return bg === INK ? WHITE : INK; }

    /* One photo, centred on (cx, cy), longest side = size. */
    function photo(ctx, fr, cx, cy, size, rot, o) {
      o = o || {};
      var sx = o.sx || 1, sy = o.sy || 1, lift = o.lift == null ? 1 : o.lift;
      var d = fit(fr, size);
      ctx.save();
      ctx.translate(cx, cy); ctx.rotate(rot);
      if (o.bottom) { ctx.translate(0, d.h / 2); ctx.scale(sx, sy); ctx.translate(0, -d.h / 2); } else { ctx.scale(sx, sy); }
      if (lift) { shadow(ctx, size * .05 * lift, size * .022 * lift, .1 + .06 * Math.min(lift, 2)); }
      ctx.fillStyle = '#ddd';
      roundRect(ctx, -d.w / 2, -d.h / 2, d.w, d.h, U * .004); ctx.fill();
      noShadow(ctx);
      ctx.save(); roundRect(ctx, -d.w / 2, -d.h / 2, d.w, d.h, U * .004); ctx.clip();
      ctx.drawImage(fr.img, -d.w / 2, -d.h / 2, d.w, d.h);
      if (o.dim) { ctx.fillStyle = 'rgba(20,20,20,' + o.dim + ')'; ctx.fillRect(-d.w / 2, -d.h / 2, d.w, d.h); }
      ctx.restore();
      if (o.label && state.names) {
        ctx.font = (U * .02) + 'px "Neue Montreal", system-ui, sans-serif';
        ctx.textBaseline = 'top'; ctx.textAlign = 'left';
        ctx.fillStyle = o.labelColour || 'rgba(20,20,20,.5)';
        ctx.fillText(fileLabel(fr.name), -d.w / 2, d.h / 2 + U * .016, d.w);
      }
      ctx.restore();
      return d;
    }

    function dot(ctx, x, y, text, f, k) {
      var j = wob(f, k), r = U * .034;
      ctx.save();
      ctx.translate(x + j.x, y + j.y); ctx.rotate(j.r);
      ctx.fillStyle = DOT; ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = INK; ctx.font = (r * .78) + 'px "Neue Montreal", system-ui, sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(text, 0, r * .04);
      ctx.restore();
    }

    function counter(ctx, W, H, i, T, bg) {
      ctx.save();
      ctx.font = (U * .022) + 'px "Neue Montreal", system-ui, sans-serif';
      ctx.fillStyle = bg === INK ? 'rgba(255,255,255,.55)' : 'rgba(20,20,20,.45)';
      ctx.textBaseline = 'alphabetic';
      var m = U * .06;
      ctx.textAlign = 'left'; ctx.fillText(String(i + 1).padStart(2, '0') + ' / ' + String(T.n).padStart(2, '0'), m, H - m);
      ctx.restore();
    }

    /* Words appear one per frame, like letters placed by hand. */
    function caption(ctx, W, H, y, f, bg, maxW) {
      var l1 = (state.line1 || '').trim(), l2 = (state.line2 || '').trim();
      if (!l1 && !l2) { return; }
      var colour = fg(bg), big = U * (H >= W ? .092 : .075);
      var words = [];
      var shown = f + 1;
      function line(text, font, spacing, yy, offset) {
        if (!text) { return; }
        ctx.font = font(big); setSpacing(ctx, spacing * big);
        var size = big, w = ctx.measureText(text).width;
        if (w > maxW) { size = big * maxW / w; ctx.font = font(size); setSpacing(ctx, spacing * size); w = ctx.measureText(text).width; }
        var parts = text.split(/\s+/), x = W / 2 - w / 2, space = ctx.measureText(' ').width;
        parts.forEach(function (p, i) {
          var pw = ctx.measureText(p).width;
          if (offset + i < shown) {
            var j = wob(f, 800 + offset + i);
            ctx.fillText(p, x + j.x * .35, yy + j.y * .35);
          }
          x += pw + space;
        });
        return parts.length;
      }
      ctx.save();
      ctx.fillStyle = colour; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      var n1 = line(l1, function (s) { return s + 'px "Neue Montreal", system-ui, sans-serif'; }, -.045, y, 0) || 0;
      line(l2, function (s) { return 'italic ' + (s * 1.08) + 'px "Instrument Serif", Georgia, serif'; }, -.02, y + big * (l1 ? 1.02 : 0), n1 + 1);
      setSpacing(ctx, 0);
      ctx.restore();
    }

    var MOVE = {
      drop: function (ctx, W, H, i, lf, f, T, bg) {
        var fr = T.frames, portrait = H >= W;
        var size = U * (portrait ? .72 : .6), cy = H * (portrait ? .46 : .47);
        function rest(k) { return { x: W / 2 + (R(k, 1) - .5) * U * .18, y: cy + (R(k, 2) - .5) * U * .1, r: (R(k, 3) - .5) * .3 }; }
        for (var k = Math.max(0, i - 3); k < i; k++) {
          var p0 = rest(k), j0 = wob(f, k);
          photo(ctx, fr[k], p0.x + j0.x * .5, p0.y + j0.y * .5, size, p0.r + j0.r * .5, { lift: .45 });
        }
        var p = rest(i), j = wob(f, i + 50), s = Math.min(lf, 4);
        var sc = [1.42, 1.16, .975, 1.01, 1][s], up = [2.4, 1.2, 0, 0, 0][s];
        var rot = p.r * [.1, .5, 1.1, 1, 1][s] + j.r;
        photo(ctx, fr[i], p.x + j.x, p.y + j.y - up * U * .02, size * sc, rot, { lift: 1 + up * .6, label: lf >= 3, labelColour: bg === INK ? 'rgba(255,255,255,.6)' : null });
        counter(ctx, W, H, i, T, bg);
      },

      flip: function (ctx, W, H, i, lf, f, T, bg) {
        var fr = T.frames, portrait = H >= W;
        var cw, ch;
        if (portrait) { cw = W * .8; ch = Math.min(cw * 1.3, H * .7); } else { ch = H * .74; cw = ch * .78; }
        var cx = W / 2, cy = H * .47, j = wob(f, 70);
        var pages = Math.min(6, T.n - i);
        for (var p = pages; p >= 1; p--) {
          ctx.fillStyle = p % 2 ? '#e7e7e7' : '#f2f2f2';
          ctx.fillRect(cx - cw / 2 + p * U * .003 + j.x * .3, cy - ch / 2 + p * U * .008, cw, ch);
        }
        function page(frm) {
          ctx.save(); ctx.beginPath(); ctx.rect(-cw / 2, -ch / 2, cw, ch); ctx.clip();
          var a = frm.w / frm.h, b = cw / ch, sw = frm.w, sh = frm.h, sx = 0, sy = 0;
          if (a > b) { sw = frm.h * b; sx = (frm.w - sw) / 2; } else { sh = frm.w / b; sy = (frm.h - sh) / 2; }
          ctx.drawImage(frm.img, sx, sy, sw, sh, -cw / 2, -ch / 2, cw, ch);
          ctx.restore();
        }
        ctx.save();
        ctx.translate(cx + j.x, cy + j.y); ctx.rotate(j.r * .4);
        shadow(ctx, U * .04, U * .015, .12); ctx.fillStyle = '#ddd'; ctx.fillRect(-cw / 2, -ch / 2, cw, ch); noShadow(ctx);
        page(fr[i]);
        if (i > 0 && lf < 3) { ctx.fillStyle = 'rgba(20,20,20,' + [.22, .12, .05][lf] + ')'; ctx.fillRect(-cw / 2, -ch / 2, cw, ch); }
        ctx.restore();
        if (i > 0 && lf < 2) {
          var k = [.55, .14][lf], skew = [-.1, -.3][lf];
          ctx.save();
          ctx.translate(cx + j.x, cy - ch / 2 + j.y);
          ctx.transform(1, 0, skew, 1, 0, 0);
          ctx.scale(1, -k);
          ctx.translate(0, -ch / 2);
          page(fr[i - 1]);
          ctx.fillStyle = 'rgba(255,255,255,' + [.25, .5][lf] + ')'; ctx.fillRect(-cw / 2, -ch / 2, cw, ch);
          ctx.restore();
        }
        if (state.names) {
          ctx.save();
          ctx.font = (U * .02) + 'px "Neue Montreal", system-ui, sans-serif';
          ctx.fillStyle = bg === INK ? 'rgba(255,255,255,.6)' : 'rgba(20,20,20,.5)';
          ctx.textBaseline = 'top';
          ctx.fillText(fileLabel(fr[i].name), cx - cw / 2 + j.x, cy + ch / 2 + U * .03 + j.y, cw);
          ctx.restore();
        }
        counter(ctx, W, H, i, T, bg);
      },

      slide: function (ctx, W, H, i, lf, f, T, bg) {
        var fr = T.frames, portrait = H >= W;
        if (i > 0) {
          var pj = wob(f, i + 40);
          photo(ctx, fr[i - 1], W / 2 + pj.x * .4, H * .47 + pj.y * .4, U * (portrait ? .66 : .56), (R(i - 1, 22) - .5) * .16, { lift: .35, dim: bg === INK ? 0 : .04 });
        }
        var dir = i % 2 ? 1 : -1, s = Math.min(lf, 3);
        var off = [1.05, .3, -.045, 0][s];
        var size = U * (portrait ? .72 : .6), r = (R(i, 22) - .5) * .16 + [dir * .2, dir * .08, -dir * .02, 0][s];
        var j = wob(f, i + 120);
        var cx = W / 2 + dir * W * off + j.x, cy = H * .47 + j.y;
        var d = photo(ctx, fr[i], cx, cy, size, r + j.r, { lift: .9, label: lf >= 3, labelColour: bg === INK ? 'rgba(255,255,255,.6)' : null });
        if (lf >= 3) {
          var pop = lf === 3 ? 1.3 : 1;
          ctx.save(); ctx.translate(cx, cy); ctx.rotate(r);
          ctx.scale(pop, pop);
          dot(ctx, d.w / 2 - U * .01, -d.h / 2 + U * .01, String(i + 1).padStart(2, '0'), f, 130);
          ctx.restore();
        }
        counter(ctx, W, H, i, T, bg);
      },

      bounce: function (ctx, W, H, i, lf, f, T, bg) {
        var fr = T.frames, portrait = H >= W;
        var size = U * (portrait ? .68 : .56);
        var d = fit(fr[i], size);
        var ground = H * .47 + d.h / 2;
        var S = [[.25, .25], [1.22, .78], [.88, 1.14], [1.05, .96], [.99, 1.01], [1, 1]];
        var s = Math.min(lf, 5), sx = S[s][0], sy = S[s][1];
        var up = [0, 0, U * .06, U * .012, 0, 0][s];
        var j = wob(f, i + 600);
        ctx.save();
        ctx.fillStyle = bg === INK ? 'rgba(255,255,255,.08)' : 'rgba(20,20,20,.08)';
        ctx.beginPath(); ctx.ellipse(W / 2 + j.x, ground + U * .02, d.w * .5 * sx * (1 - up / U * 2.5), U * .02, 0, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
        photo(ctx, fr[i], W / 2 + j.x, ground - d.h / 2 - up + j.y * .3, size, j.r * .5, { sx: sx, sy: sy, bottom: true, lift: .35 });
        if (lf === 1) {
          ctx.save();
          ctx.strokeStyle = fg(bg); ctx.globalAlpha = .5; ctx.lineWidth = U * .005; ctx.lineCap = 'round';
          [-1, 1].forEach(function (side) {
            for (var q = 0; q < 3; q++) {
              var x0 = W / 2 + side * (d.w * .5 * sx + U * .03), y0 = ground - U * (.02 + q * .04);
              ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x0 + side * U * .05, y0 - U * .015 * (q - 1)); ctx.stroke();
            }
          });
          ctx.restore();
        }
        if (lf >= 3 && state.names) {
          ctx.save();
          ctx.font = (U * .02) + 'px "Neue Montreal", system-ui, sans-serif';
          ctx.fillStyle = bg === INK ? 'rgba(255,255,255,.6)' : 'rgba(20,20,20,.5)';
          ctx.textAlign = 'center'; ctx.textBaseline = 'top';
          ctx.fillText(fileLabel(fr[i].name), W / 2, ground + U * .06, W * .8);
          ctx.restore();
        }
        counter(ctx, W, H, i, T, bg);
      }
    };

    function finale(ctx, W, H, fl, T, bg) {
      var n = T.n, portrait = H >= W;
      var hasWords = !!((state.line1 || '').trim() || (state.line2 || '').trim());
      var cols = portrait ? (n === 1 ? 1 : n <= 6 ? 2 : 3) : (n <= 4 ? n : (n <= 8 ? 4 : 5));
      var rows = Math.ceil(n / cols);
      var ax = W * .08, aw = W * .84;
      var ay = H * (portrait ? .06 : .07), ah = H * (hasWords ? (portrait ? .64 : .58) : .86);
      var cw = aw / cols, ch = ah / rows;
      for (var k = 0; k < n; k++) {
        if (fl < k) { continue; }
        var a = T.frames[k].w / T.frames[k].h;
        var size = (a >= 1 ? Math.min(cw * .88, ch * .88 * a) : Math.min(ch * .88, cw * .88 / a));
        var lf = fl - k, row = Math.floor(k / cols);
        var inRow = row === rows - 1 ? n - row * cols : cols;
        var x = ax + (cols - inRow) * cw / 2 + cw * (k % cols + .5), y = ay + ch * (row + .5);
        var sc = [.5, 1.14, .96, 1][Math.min(lf, 3)];
        var j = wob(fl, 700 + k);
        photo(ctx, T.frames[k], x + j.x * .5, y + j.y * .5, size * sc, (R(k, 31) - .5) * .12 + j.r * .5, { lift: .35 });
      }
      if (hasWords && fl >= n + 1) {
        caption(ctx, W, H, H * (portrait ? .77 : .76), fl - n - 1, bg, W * .84);
      }
    }

    function draw(ctx, W, H, f, scale) {
      SC = scale || 1; U = Math.min(W, H);
      var T = timing(), bg = find(GROUNDS, state.ground).c;
      ctx.save();
      ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
      if (!T.n) {
        ctx.fillStyle = fg(bg); ctx.font = (U * .04) + 'px "Neue Montreal", system-ui, sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(state.loading ? 'Loading your images' : 'Pick at least one image', W / 2, H / 2);
      } else if (f >= T.n * T.hold) {
        finale(ctx, W, H, f - T.n * T.hold, T, bg);
      } else {
        MOVE[state.move](ctx, W, H, Math.floor(f / T.hold), f % T.hold, f, T, bg);
      }
      ctx.fillStyle = 'rgba(20,16,10,' + (R(f, 9) * .03) + ')';
      ctx.fillRect(0, 0, W, H);
      if (state.grain) {
        var gp = ctx.createPattern(grain[f % 3], 'repeat');
        if (gp && gp.setTransform && window.DOMMatrix) { gp.setTransform(new DOMMatrix().translate(R(f, 3) * 256, R(f, 4) * 256).scale(U / 600)); }
        ctx.globalCompositeOperation = 'overlay'; ctx.globalAlpha = .28;
        ctx.fillStyle = gp; ctx.fillRect(0, 0, W, H);
      }
      ctx.restore();
    }

    return { draw: draw, timing: timing };
  }

  /* ------------------------------------------------------------------ export */
  /* ~10 Mbps at 1080x1920, scaled by pixels. 6 Mbps was landing at about 3 Mbps on an iPhone (25 Sep, Kaitlin:
     "the quality kinda seems worse"), because the config Safari hands back drops the bitrate and its own default
     takes over. Crumpled paper and newsprint are the worst case for a low bitrate: the texture turns to mush. */
  /* 6 Oct: raised from x5/16M. Crumpled paper and newsprint need the headroom; a 2.7s clip is still a few MB. */
  function videoBitrate(W, H) { return Math.max(6e6, Math.min(20e6, Math.round(W * H * 8))); }
  function pickVideo(W, H, fps) {
    if (typeof VideoEncoder === 'undefined') { return Promise.resolve(null); }
    var codecs = ['avc1.640034', 'avc1.640033', 'avc1.640028', 'avc1.4d0034', 'avc1.42003e'];
    var i = 0;
    function next() {
      if (i >= codecs.length) { return Promise.resolve(null); }
      var cfg = { codec: codecs[i++], width: W, height: H, bitrate: videoBitrate(W, H), framerate: fps };
      return VideoEncoder.isConfigSupported(cfg).then(function (r) {
        if (!r.supported) { return next(); }
        /* Take what it says it will do, then put OUR numbers back: a config that came home without a bitrate is
           what left the video at the encoder's own default. */
        var out = r.config || {};
        out.codec = out.codec || cfg.codec; out.width = W; out.height = H;
        out.bitrate = cfg.bitrate; out.framerate = fps;
        return out;
      }, next);
    }
    return next();
  }

  /* The sample rate has to be one the encoder will really use. Safari says yes to 48k mono and then encodes at
     22050 (25 Sep: her video carried an AAC track at 22050 Hz, 0 channels, which no player could decode, so it
     played silent). So we ask, take the config it hands back, and run everything at THAT rate. */
  function pickAudio() {
    if (typeof AudioEncoder === 'undefined' || typeof OfflineAudioContext === 'undefined') { return Promise.resolve(null); }
    var rates = [48000, 44100], i = 0;
    function next() {
      if (i >= rates.length) { return Promise.resolve(null); }
      var cfg = { codec: 'mp4a.40.2', sampleRate: rates[i++], numberOfChannels: 1, bitrate: 128000 };
      return AudioEncoder.isConfigSupported(cfg).then(function (r) {
        if (!r.supported) { return next(); }
        var out = r.config || {};
        return { codec: out.codec || cfg.codec, sampleRate: out.sampleRate || cfg.sampleRate,
          numberOfChannels: out.numberOfChannels || cfg.numberOfChannels, bitrate: cfg.bitrate };
      }, next);
    }
    return next();
  }

  function isAudioConfig(d) {
    if (!d) { return false; }
    var v;
    try { v = d instanceof ArrayBuffer ? new Uint8Array(d) : new Uint8Array(d.buffer || d, d.byteOffset || 0, d.byteLength); } catch (e) { return false; }
    return v.length >= 2 && v.length <= 8 && (v[0] >> 3) > 0;
  }

  /* painter.audio(ctx) is optional: schedule sounds on the OfflineAudioContext it is given.
     The chunks are collected rather than muxed straight away, because the muxer has to be told the REAL sample
     rate and channel count before it is built, and only the encoder's own decoderConfig knows them. A track with
     no decoderConfig.description cannot be written as valid AAC, so we say so and ship a clean silent video
     instead of a broken sound track. */
  function encodeAudio(painter, cfg, seconds) {
    var rate = cfg.sampleRate, ch = cfg.numberOfChannels || 1;
    var oac = new OfflineAudioContext(ch, Math.ceil(seconds * rate), rate);
    painter.audio(oac);
    return oac.startRendering().then(function (buf) {
      var data = buf.getChannelData(0), failed = null, CH = Math.round(rate / 10), got = [], conf = null;
      var enc = new AudioEncoder({
        output: function (c, m) { if (m && m.decoderConfig) { conf = m.decoderConfig; } got.push({ chunk: c, meta: m }); },
        error: function (e) { failed = e; }
      });
      enc.configure(cfg);
      for (var s = 0; s < data.length; s += CH) {
        var part = data.slice(s, Math.min(s + CH, data.length));
        var ad = new AudioData({ format: 'f32', sampleRate: rate, numberOfFrames: part.length, numberOfChannels: ch, timestamp: Math.round(s / rate * 1e6), data: part });
        enc.encode(ad); ad.close();
      }
      return enc.flush().then(function () {
        enc.close();
        if (failed) { throw failed; }
        /* The description has to be a bare AudioSpecificConfig: 2 to 5 bytes whose first 5 bits are the object
           type. Safari hands back a whole ES descriptor blob instead (it starts 0x03), which writes a track that
           says "object type 0" and plays silent. Her frames did not decode even with a corrected header, so
           WebCodecs audio is not usable there at all: say no and let the recorder do the sound. */
        if (!got.length || !conf || !isAudioConfig(conf.description)) { return null; }
        return { chunks: got, sampleRate: conf.sampleRate || rate, numberOfChannels: conf.numberOfChannels || ch };
      });
    }).catch(function () { return null; });
  }

  function encodeMp4(painter, W, H, onProgress) {
    var OUT = 30, acfg = null;
    return Promise.all([pickVideo(W, H, OUT), painter.audio ? pickAudio() : Promise.resolve(null)]).then(function (picked) {
      var cfg = picked[0]; acfg = picked[1];
      if (!cfg) { return null; }
      return (window.Mp4Muxer ? Promise.resolve() : loadScript(MUXER_SRC)).then(function () {
        if (!window.Mp4Muxer) { return null; }
        var T = painter.timing(), seconds = T.total / T.F, count = Math.ceil(seconds * OUT);
        /* The sound is made first, because the muxer has to be told the rate and channels the encoder really
           used. No usable sound means no audio track at all, rather than one that plays as silence. */
        return (acfg ? encodeAudio(painter, acfg, seconds) : Promise.resolve(null)).then(function (sound) {
        /* Wanted sound and WebCodecs couldn't give us a valid track (Safari, every iPhone): record the sound on its
           own instead, while the picture is still made frame by frame here. Before 3 Nov the whole video went to the
           real-time recorder in that case, so every iPhone video with sound was screen-recorded: dropped frames on
           a busy phone and whatever bitrate Safari felt like (Kaitlin: "i dont want anyone complaining about
           quality ever"). The recorder is now only for the picture when there is no VideoEncoder at all. */
        var later = painter.audio && !sound ? recordAudio(painter, seconds) : null;
        var canvas = document.createElement('canvas'); canvas.width = W; canvas.height = H;
        var ctx = canvas.getContext('2d');
        var failed = null, video = [];
        /* Chunks are kept, not muxed straight away: the muxer has to be told about the sound before it is built,
           and recorded sound only arrives once it has played through. */
        var enc = new VideoEncoder({ output: function (c, m) { video.push({ chunk: c, meta: m }); }, error: function (e) { failed = e; } });
        enc.configure(cfg);
        var k = 0, t0 = performance.now();
        function progress(v) {
          if (!later) { onProgress(v); return; }
          onProgress(Math.min(v, (performance.now() - t0) / 1000 / (seconds + .5)) * .98);
        }
        return new Promise(function (resolve, reject) {
          function step() {
            if (failed) { reject(failed); return; }
            var until = Math.min(count, k + 6);
            for (; k < until; k++) {
              ctx.setTransform(1, 0, 0, 1, 0, 0);
              painter.draw(ctx, W, H, Math.min(T.total - 1, Math.floor(k / OUT * T.F)), 1);
              var vf = new VideoFrame(canvas, { timestamp: Math.round(k * 1e6 / OUT), duration: Math.round(1e6 / OUT) });
              enc.encode(vf, { keyFrame: k % 60 === 0 });
              vf.close();
            }
            progress(k / count);
            if (k < count) { setTimeout(step, enc.encodeQueueSize > 12 ? 30 : 0); return; }
            enc.flush().then(function () {
              enc.close();
              if (failed) { throw failed; }
              var tick = later ? setInterval(function () { progress(1); }, 200) : 0;
              return Promise.resolve(later).then(function (rec) {
                clearInterval(tick);
                if (rec) { sound = rec; }
                /* No sound by any route: the recorder can still try (it plays the beats into a screen recording),
                   and only when it can't either is the video silent. */
                if (painter.audio && !sound) {
                  if (recorderCanSound()) { return null; }
                  painter.silent = true;
                }
                var muxer = new window.Mp4Muxer.Muxer({
                  target: new window.Mp4Muxer.ArrayBufferTarget(),
                  video: { codec: 'avc', width: W, height: H, frameRate: OUT },
                  audio: sound ? { codec: 'aac', numberOfChannels: sound.numberOfChannels, sampleRate: sound.sampleRate } : undefined,
                  fastStart: 'in-memory'
                });
                video.forEach(function (v) { muxer.addVideoChunk(v.chunk, v.meta); });
                if (sound) { sound.chunks.forEach(function (a) { if (a.raw) { muxer.addAudioChunkRaw(a.raw, 'key', a.ts, a.dur, a.meta); } else { muxer.addAudioChunk(a.chunk, a.meta); } }); }
                muxer.finalize();
                return new Blob([muxer.target.buffer], { type: 'video/mp4' });
              });
            }).then(resolve, reject);
          }
          step();
        });
        });
      });
    });
  }

  /* Can the MediaRecorder path carry sound? It needs a live AudioContext and a recording format that takes audio. */
  function recorderCanSound() {
    if (typeof MediaRecorder === 'undefined' || typeof AudioContext === 'undefined' || typeof OfflineAudioContext === 'undefined') { return false; }
    return ['video/mp4;codecs=avc1,mp4a.40.2', 'video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm'].some(function (m) { return MediaRecorder.isTypeSupported(m); });
  }

  /* Sound for the recorder: the same beats, rendered offline first, then played into the recording as it runs.
     Browsers without AudioEncoder (Safari, iPhones) get sound this way instead of a silent video (23 Sep). */
  function soundTrack(painter, seconds) {
    if (!painter.audio || typeof OfflineAudioContext === 'undefined' || typeof AudioContext === 'undefined') { return Promise.resolve(null); }
    var oac = new OfflineAudioContext(1, Math.ceil(seconds * 48000), 48000);
    painter.audio(oac);
    return oac.startRendering().then(function (buf) {
      /* Always use the editor's own context when there is one, running or not, and wait for it to wake up. Only
         taking it when it was ALREADY running was the bug: resume() is asynchronous, so a context unlocked a
         moment earlier in the tap still reads "suspended" here, and we were throwing it away for a fresh one that
         had no gesture behind it and could never start. Measured on her Mac (28 Sep): Safari has no AudioEncoder
         at all, took the recorder, and the video came out silent for exactly this reason. */
      var mine = !painter.ac;
      var ac = painter.ac || new (window.AudioContext || window.webkitAudioContext)();
      var dest = ac.createMediaStreamDestination(), src = ac.createBufferSource();
      src.buffer = buf; src.connect(dest);
      return Promise.resolve(ac.state === 'running' ? null : ac.resume()).catch(function () {}).then(function () {
        if (ac.state !== 'running') { if (mine) { ac.close(); } return null; }
        return { buf: buf, track: dest.stream.getAudioTracks()[0], start: function () { src.start(); }, stop: function () { try { src.stop(); } catch (e) { /* already done */ } try { src.disconnect(); } catch (e) { /* ignore */ } if (mine) { ac.close(); } } };
      });
    }).catch(function () { return null; });
  }

  /* The sound on its own, recorded as AAC by the browser's recorder (which Safari does properly, unlike its AAC
     AudioEncoder), then taken out of that little MP4 so it can go into ours beside a frame-by-frame picture.
     Takes as long as the video plays; the picture is made meanwhile. */
  function recordAudio(painter, seconds) {
    if (typeof MediaRecorder === 'undefined' || typeof MediaStream === 'undefined') { return Promise.resolve(null); }
    var mime = ['audio/mp4;codecs=mp4a.40.2', 'audio/mp4'].filter(function (m) { return MediaRecorder.isTypeSupported(m); })[0];
    if (!mime) { return Promise.resolve(null); }
    var lib = window.MP4Box ? Promise.resolve() : loadScript(MP4BOX_SRC);
    return soundTrack(painter, seconds).then(function (sound) {
      if (!sound) { return null; }
      return new Promise(function (resolve) {
        var rec = new MediaRecorder(new MediaStream([sound.track]), { mimeType: mime, audioBitsPerSecond: 128000 }), parts = [];
        rec.ondataavailable = function (e) { if (e.data.size) { parts.push(e.data); } };
        rec.onerror = function () { sound.stop(); resolve(null); };
        rec.onstop = function () { sound.stop(); resolve({ blob: new Blob(parts, { type: mime }), want: sound.buf }); };
        /* The sound starts when the recording really has, so the beats sit where the picture expects them. */
        rec.onstart = function () { sound.start(); setTimeout(function () { rec.stop(); }, seconds * 1000 + 250); };
        rec.start();
      });
    }).then(function (got) {
      if (!got) { return null; }
      return lib.then(function () { return got.blob.arrayBuffer(); }).then(function (buf) {
        return lagOf(buf, got.want).then(function (lag) { return demuxAac(buf, seconds, lag); });
      });
    }).catch(function () { return null; });
  }

  /* A recording starts a little after it says it has (0.17s in Chrome, 3 Nov), which puts every beat late against
     the picture. So the recording is decoded and its first sound matched to the first sound we asked for, and the
     difference is cut off the front. Good to one AAC frame (~23ms). */
  function lagOf(buf, want) {
    function onset(b) {
      var d = b.getChannelData(0), peak = 0, i;
      for (i = 0; i < d.length; i++) { if (Math.abs(d[i]) > peak) { peak = Math.abs(d[i]); } }
      if (peak < 1e-4) { return -1; }
      for (i = 0; i < d.length; i++) { if (Math.abs(d[i]) > peak * .1) { return i / b.sampleRate; } }
      return -1;
    }
    try {
      var oac = new OfflineAudioContext(1, 1, want.sampleRate);
      return oac.decodeAudioData(buf.slice(0)).then(function (got) {
        var a = onset(want), b = onset(got);
        return a < 0 || b < 0 || b - a < 0 || b - a > 1 ? 0 : b - a;
      }, function () { return 0; });
    } catch (e) { return Promise.resolve(0); }
  }

  function demuxAac(buf, seconds, lag) {
    if (!window.MP4Box) { return null; }
    var file = window.MP4Box.createFile(), track = null, samples = [];
    file.onError = function () { track = null; };
    file.onReady = function (info) {
      track = info.audioTracks && info.audioTracks[0];
      if (track) { file.setExtractionOptions(track.id, null, { nbSamples: 1e6 }); file.start(); }
    };
    file.onSamples = function (id, user, list) { samples.push.apply(samples, list); };
    buf.fileStart = 0;
    file.appendBuffer(buf);
    file.flush();
    if (!track || !samples.length) { return null; }
    var asc = null;
    try {
      var entry = file.getTrackById(track.id).mdia.minf.stbl.stsd.entries[0];
      asc = entry.esds.esd.descs[0].descs[0].data;
    } catch (e) { return null; }
    if (!isAudioConfig(asc)) { return null; }
    var rate = track.audio.sample_rate, ch = track.audio.channel_count || 1;
    var conf = { codec: 'mp4a.40.2', sampleRate: rate, numberOfChannels: ch, description: new Uint8Array(asc) };
    var t0 = samples[0].cts, out = [], cut = Math.round((lag || 0) * 1e6), first = true, base = 0;
    samples.forEach(function (s) {
      var ts = Math.round((s.cts - t0) / s.timescale * 1e6) - cut, dur = Math.round(s.duration / s.timescale * 1e6);
      /* Whole AAC frames only, so the frame nearest the cut becomes 0 (the muxer insists the track starts there). */
      if (first && ts < -dur / 2) { return; }
      if (first) { base = ts; }
      ts -= base;
      if (ts >= seconds * 1e6) { return; }
      /* Raw, not an EncodedAudioChunk: a Safari with VideoEncoder but no audio side of WebCodecs has none. */
      out.push({ raw: new Uint8Array(s.data), ts: ts, dur: dur, meta: first ? { decoderConfig: conf } : undefined });
      first = false;
    });
    return out.length ? { chunks: out, sampleRate: rate, numberOfChannels: ch } : null;
  }

  function recordRealtime(painter, W, H, onProgress) {
    var canvas = document.createElement('canvas'); canvas.width = W; canvas.height = H;
    if (!canvas.captureStream || typeof MediaRecorder === 'undefined') { return Promise.reject(new Error('This browser can’t make videos. Try Chrome or Safari.')); }
    var ctx = canvas.getContext('2d');
    var T = painter.timing();
    return soundTrack(painter, T.total / T.F).then(function (sound) { return record(painter, canvas, ctx, T, sound, onProgress); });
  }
  function record(painter, canvas, ctx, T, sound, onProgress) {
    /* Assign both ways: this runs after the WebCodecs path has given up, and it is the recorder that decides
       whether there is sound in the end. Setting silent earlier, when the AAC encoder was missing, left the panel
       telling a Safari customer their video was silent when the recorder had in fact put sound in it (25 Sep). */
    painter.silent = !!painter.audio && !sound;
    var mime = (sound
      ? ['video/mp4;codecs=avc1,mp4a.40.2', 'video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm']
      : ['video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm']).filter(function (m) { return MediaRecorder.isTypeSupported(m); })[0] || '';
    var stream = canvas.captureStream(30);
    if (sound) { stream.addTrack(sound.track); }
    var rec = new MediaRecorder(stream, mime ? { mimeType: mime, videoBitsPerSecond: 16000000 } : undefined);
    var chunks = [];
    rec.ondataavailable = function (e) { if (e.data.size) { chunks.push(e.data); } };
    return new Promise(function (resolve) {
      rec.onstop = function () { if (sound) { sound.stop(); } resolve(new Blob(chunks, { type: mime.split(';')[0] || 'video/webm' })); };
      rec.start(250);
      if (sound) { sound.start(); }
      var start = performance.now(), last = -1;
      (function tick() {
        var f = Math.floor((performance.now() - start) / 1000 * T.F);
        if (f >= T.total) { rec.stop(); return; }
        /* The size comes off the canvas: W and H are not in scope here, and referencing them threw "W is not
           defined" on every browser that took this path (found 25 Sep while testing the Safari fallback). */
        if (f !== last) { ctx.setTransform(1, 0, 0, 1, 0, 0); painter.draw(ctx, canvas.width, canvas.height, f, 1); last = f; onProgress(f / T.total); }
        requestAnimationFrame(tick);
      })();
    });
  }

  /* ------------------------------------------------------------------ UI */
  var current = null;

  function open(opts) {
    if (current) { current.close(); }
    opts = opts || {};
    var images = (opts.images || []).filter(function (x) { return x && x.url; });
    var state = {
      frames: images.map(function (x, i) { return { url: x.url, name: x.name || '', on: i < MAX, img: null, w: 1, h: 1 }; }),
      move: 'drop', pace: 'steady', feel: 'classic', size: '9x16', ground: 'white',
      line1: opts.line1 || '', line2: opts.line2 || '', names: true, grain: true, loading: true
    };
    var painter = Painter(state);
    var prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';

    var root = el('div', 'mx-motion');
    root.setAttribute('role', 'dialog'); root.setAttribute('aria-modal', 'true'); root.setAttribute('aria-label', 'Make a video');
    root.innerHTML =
      '<div class="mx-motion__stage">' +
        '<div class="mx-motion__screen"><canvas class="mx-motion__canvas" aria-label="Video preview"></canvas></div>' +
        '<div class="mx-motion__bar">' +
          '<button type="button" class="mx-motion__play" data-play aria-label="Pause"></button>' +
          '<input type="range" class="mx-motion__scrub" data-scrub min="0" max="1" value="0" aria-label="Move through the video">' +
          '<span class="mx-motion__time" data-time>0.0s</span>' +
        '</div>' +
      '</div>' +
      '<div class="mx-motion__panel">' +
        '<div class="mx-motion__head"><p class="mx-motion__title">Make it <em>move.</em></p><button type="button" class="mx-motion__close" data-close>Close</button></div>' +
        '<p class="mx-motion__lede">Your mockups, shot frame by frame. It’s made on your device, so it doesn’t use any downloads.</p>' +
        '<div class="mx-motion__group"><p class="mx-motion__label"><span>Images</span><span data-count></span></p><div class="mx-motion__frames" data-frames></div><p class="mx-motion__hint">Tap one to leave it out. Drag to change the order. Up to 10.</p></div>' +
        '<div class="mx-motion__group"><p class="mx-motion__label">Movement</p><div class="mx-motion__pills" data-pills="move"></div></div>' +
        '<div class="mx-motion__group"><p class="mx-motion__label">Pace</p><div class="mx-motion__pills" data-pills="pace"></div></div>' +
        '<div class="mx-motion__group"><p class="mx-motion__label">Feel</p><div class="mx-motion__pills" data-pills="feel"></div></div>' +
        '<div class="mx-motion__group"><p class="mx-motion__label">Size</p><div class="mx-motion__pills" data-pills="size"></div></div>' +
        '<div class="mx-motion__group"><p class="mx-motion__label">Background</p><div class="mx-motion__swatches" data-swatches></div></div>' +
        '<div class="mx-motion__group"><p class="mx-motion__label">Words at the end</p>' +
          '<input class="mx-motion__input" data-line="line1" maxlength="32" placeholder="First line" aria-label="First line">' +
          '<input class="mx-motion__input mx-motion__input--serif" data-line="line2" maxlength="32" placeholder="Second line, in italics" aria-label="Second line">' +
        '</div>' +
        '<div class="mx-motion__group">' +
          '<label class="mx-motion__toggle">File names under each image <input type="checkbox" data-opt="names" checked></label>' +
          '<label class="mx-motion__toggle">Film grain <input type="checkbox" data-opt="grain" checked></label>' +
        '</div>' +
        '<div class="mx-motion__foot">' +
          '<p class="mx-motion__meta" data-meta></p>' +
          '<button type="button" class="mx-motion__make" data-make>Make video</button>' +
          '<div class="mx-motion__progress" data-progress hidden><i></i></div>' +
          '<p class="mx-motion__status" data-status role="status"></p>' +
          '<div class="mx-motion__result" data-result hidden><video playsinline controls loop muted></video><button type="button" class="mx-motion__save" data-save>Download video</button></div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(root);

    var q = function (s) { return root.querySelector(s); };
    var canvas = q('canvas'), ctx = canvas.getContext('2d'), scale = .5;
    var playBtn = q('[data-play]'), scrub = q('[data-scrub]'), timeEl = q('[data-time]');
    var playing = !matchMedia('(prefers-reduced-motion: reduce)').matches;
    var frame = 0, t0 = performance.now(), last = -1, dirty = true, raf = 0, busy = false, blob = null, blobUrl = null;

    function sizeCanvas() {
      var s = find(SIZES, state.size);
      scale = Math.min(1, 1280 / Math.max(s.W, s.H));
      canvas.width = Math.round(s.W * scale); canvas.height = Math.round(s.H * scale);
      dirty = true;
    }
    function setPlaying(p) {
      playing = p;
      var T = painter.timing();
      if (p) { t0 = performance.now() - frame / T.F * 1000; }
      playBtn.setAttribute('aria-label', p ? 'Pause' : 'Play');
      playBtn.innerHTML = p
        ? '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><rect x="2" y="1.5" width="3.4" height="11" fill="currentColor"/><rect x="8.6" y="1.5" width="3.4" height="11" fill="currentColor"/></svg>'
        : '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M3 1.5v11l9.5-5.5z" fill="currentColor"/></svg>';
    }
    function changed() {
      var T = painter.timing();
      if (frame >= T.total) { frame = 0; }
      if (playing) { t0 = performance.now() - frame / T.F * 1000; }
      dirty = true;
      scrub.max = String(Math.max(0, T.total - 1));
      var s = find(SIZES, state.size);
      q('[data-meta]').textContent = T.n ? (T.total / T.F).toFixed(1) + ' seconds · ' + s.W + ' × ' + s.H + ' MP4' : '';
      q('[data-count]').textContent = T.n + ' of ' + Math.min(MAX, state.frames.length);
      q('[data-make]').disabled = busy || !T.n;
    }
    function loop(now) {
      var T = painter.timing();
      if (playing && T.n) { frame = Math.floor((now - t0) / 1000 * T.F) % T.total; }
      if (frame !== last || dirty) {
        var s = find(SIZES, state.size);
        ctx.setTransform(scale, 0, 0, scale, 0, 0);
        painter.draw(ctx, s.W, s.H, frame, scale);
        scrub.max = String(Math.max(0, T.total - 1)); scrub.value = String(frame);
        timeEl.textContent = (frame / T.F).toFixed(1) + 's';
        last = frame; dirty = false;
      }
      raf = requestAnimationFrame(loop);
    }

    /* images */
    function drawFrames() {
      var wrap = q('[data-frames]'); wrap.innerHTML = '';
      var dragFrom = -1, n = 0;
      state.frames.forEach(function (fr, idx) {
        var b = el('button', 'mx-motion__frame');
        b.type = 'button'; b.draggable = true;
        if (fr.on) { n++; }
        b.setAttribute('aria-pressed', String(fr.on));
        b.setAttribute('aria-label', (fr.on ? 'Leave out ' : 'Put back ') + (fr.name || 'image ' + (idx + 1)));
        b.innerHTML = '<img alt="" src="' + esc(fr.url) + '">' + (fr.on ? '<span>' + n + '</span>' : '<span>–</span>');
        b.addEventListener('click', function () {
          var count = state.frames.filter(function (x) { return x.on; }).length;
          if (!fr.on && count >= MAX) { status('That’s 10. Leave one out first.'); return; }
          fr.on = !fr.on; drawFrames(); changed();
        });
        b.addEventListener('dragstart', function (e) { dragFrom = idx; b.classList.add('is-dragging'); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(idx)); });
        b.addEventListener('dragend', function () { b.classList.remove('is-dragging'); });
        b.addEventListener('dragover', function (e) { e.preventDefault(); b.classList.add('is-over'); });
        b.addEventListener('dragleave', function () { b.classList.remove('is-over'); });
        b.addEventListener('drop', function (e) {
          e.preventDefault();
          if (dragFrom < 0 || dragFrom === idx) { b.classList.remove('is-over'); return; }
          var moved = state.frames.splice(dragFrom, 1)[0];
          state.frames.splice(idx, 0, moved);
          drawFrames(); changed();
        });
        wrap.appendChild(b);
      });
    }
    function loadAll() {
      var failed = 0;
      return Promise.all(state.frames.map(function (fr) {
        return new Promise(function (res) {
          var im = new Image();
          im.crossOrigin = 'anonymous';
          im.decoding = 'async';
          im.onload = function () { fr.img = im; fr.w = im.naturalWidth; fr.h = im.naturalHeight; res(); };
          im.onerror = function () { failed++; fr.on = false; res(); };
          im.src = fr.url;
        });
      })).then(function () {
        state.frames = state.frames.filter(function (fr) { return fr.img; });
        state.loading = false;
        if (failed) { status(failed === 1 ? 'One image couldn’t be loaded, so it’s been left out.' : failed + ' images couldn’t be loaded, so they’ve been left out.'); }
        drawFrames(); changed();
      });
    }

    /* controls */
    function pills(key, list) {
      var wrap = q('[data-pills="' + key + '"]');
      list.forEach(function (o) {
        var b = el('button', 'mx-motion__pill', esc(o.label)); b.type = 'button';
        b.setAttribute('aria-pressed', String(state[key] === o.id));
        b.addEventListener('click', function () {
          state[key] = o.id;
          Array.prototype.forEach.call(wrap.children, function (c) { c.setAttribute('aria-pressed', 'false'); });
          b.setAttribute('aria-pressed', 'true');
          if (key === 'size') { sizeCanvas(); }
          if (key === 'move') { frame = 0; }
          changed();
        });
        wrap.appendChild(b);
      });
    }
    pills('move', MOVES); pills('pace', PACES); pills('feel', FEELS); pills('size', SIZES);
    var sw = q('[data-swatches]');
    GROUNDS.forEach(function (g) {
      var b = el('button', 'mx-motion__swatch'); b.type = 'button';
      b.style.background = g.c; b.title = g.label; b.setAttribute('aria-label', g.label);
      b.setAttribute('aria-pressed', String(state.ground === g.id));
      b.addEventListener('click', function () {
        state.ground = g.id;
        Array.prototype.forEach.call(sw.children, function (c) { c.setAttribute('aria-pressed', 'false'); });
        b.setAttribute('aria-pressed', 'true'); changed();
      });
      sw.appendChild(b);
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-line]'), function (inp) {
      inp.value = state[inp.getAttribute('data-line')];
      inp.addEventListener('input', function () { state[inp.getAttribute('data-line')] = inp.value; dirty = true; });
      inp.addEventListener('focus', function () {
        var T = painter.timing();
        if (T.n) { setPlaying(false); frame = T.total - 1; dirty = true; }
      });
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-opt]'), function (inp) {
      inp.addEventListener('change', function () { state[inp.getAttribute('data-opt')] = inp.checked; dirty = true; });
    });

    playBtn.addEventListener('click', function () { setPlaying(!playing); });
    scrub.addEventListener('input', function () { setPlaying(false); frame = +scrub.value; dirty = true; });

    /* export */
    function status(t) { q('[data-status]').textContent = t; }
    function progress(p) { q('[data-progress] i').style.width = (p * 100).toFixed(1) + '%'; }
    q('[data-make]').addEventListener('click', function () {
      if (busy) { return; }
      busy = true; changed();
      var make = q('[data-make]'), was = playing;
      setPlaying(false);
      make.textContent = 'Making your video…';
      q('[data-progress]').hidden = false; progress(0);
      q('[data-result]').hidden = true; status('');
      var s = find(SIZES, state.size);
      encode(painter, s.W, s.H, progress)
        .then(function (b) {
          blob = b;
          if (blobUrl) { URL.revokeObjectURL(blobUrl); }
          blobUrl = URL.createObjectURL(b);
          var v = q('[data-result] video'); v.src = blobUrl; v.play().catch(function () {});
          q('[data-result]').hidden = false;
          status('Ready. ' + (b.size / 1048576).toFixed(1) + ' MB.');
          q('[data-result]').scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        })
        .catch(function (e) {
          var msg = e && e.name === 'SecurityError'
            ? 'The browser wouldn’t let these images be saved into a video.'
            : (e && e.message) || 'Something stopped the video being made.';
          status(msg + ' Try again, or try Chrome.');
        })
        .then(function () {
          busy = false; make.textContent = 'Make video again';
          q('[data-progress]').hidden = true; changed();
          if (was) { setPlaying(true); }
        });
    });
    q('[data-save]').addEventListener('click', function () {
      if (!blob) { return; }
      var a = document.createElement('a');
      a.href = blobUrl; a.download = 'mock-exchange-video.' + (blob.type.indexOf('mp4') > -1 ? 'mp4' : 'webm');
      document.body.appendChild(a); a.click(); a.remove();
    });

    function onKey(e) {
      if (e.key === 'Escape' && !busy) { close(); }
      if (e.code === 'Space' && !/INPUT|BUTTON|VIDEO/.test(e.target.tagName)) { e.preventDefault(); setPlaying(!playing); }
    }
    function close() {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
      if (blobUrl) { URL.revokeObjectURL(blobUrl); }
      root.remove();
      document.documentElement.style.overflow = prevOverflow;
      current = null;
      if (opts.onClose) { opts.onClose(); }
    }
    q('[data-close]').addEventListener('click', close);
    document.addEventListener('keydown', onKey);

    sizeCanvas(); setPlaying(playing); drawFrames(); changed();
    raf = requestAnimationFrame(loop);
    var fonts = document.fonts ? Promise.all(['40px "Neue Montreal"', 'italic 40px "Instrument Serif"'].map(function (f) { return document.fonts.load(f).catch(function () {}); })) : Promise.resolve();
    Promise.all([fonts, loadAll()]).then(function () { frame = 0; t0 = performance.now(); dirty = true; });
    q('[data-close]').focus();

    current = { close: close };
    return current;
  }

  /* Shared with me-reveal.js. painter = { timing() -> { F, total }, draw(ctx, W, H, frame, scale) }. */
  function encode(painter, W, H, onProgress) {
    return encodeMp4(painter, W, H, onProgress).then(function (b) { return b || recordRealtime(painter, W, H, onProgress); });
  }

  /* Can this browser put sound in the MP4? Sound needs BOTH encoders: without WebCodecs the video is recorded in
     real time, which is picture only. Asked before anything is made (23 Sep, customer Camilla: "the first video I chose
     sound but it downloaded without"), so the editor can say so up front instead of after. */
  function canSound(W, H) {
    return pickVideo(W || 1080, H || 1920, 30).then(function (v) {
      /* WebCodecs: needs the AAC encoder. Recorder: needs an AudioContext and a recorder format that takes sound. */
      /* Either route will do: WebCodecs with an AAC encoder, or the recorder. If the AAC track turns out unusable
         at export time (Safari, 25 Sep) the recorder takes over, so saying yes here is still honest. */
      if (v) { return pickAudio().then(function (a) { return !!a || recorderCanSound(); }); }
      return recorderCanSound();
    }).then(function (ok) { return !!ok; }, function () { return false; });
  }
  window.MeMotion = { open: open, max: MAX, encode: encode, canSound: canSound };
})();
